import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import evidenceAPI from '../services/evidenceAPI';
import type {
  EvidenceSource,
  EvidenceItem,
  EvidenceFilters as Filters,
  EvidenceStats
} from '../types/evidence';
import EvidenceSourceGrid from '../components/Evidence/EvidenceSourceGrid';
import EvidenceList from '../components/Evidence/EvidenceList';
import EvidenceFilters from '../components/Evidence/EvidenceFilters';
import EvidenceDetailModal from '../components/Evidence/EvidenceDetailModal';
import AddSourceModal from '../components/Evidence/AddSourceModal';
import UploadEvidenceModal from '../components/Evidence/UploadEvidenceModal';
import CaptureScreenshotModal from '../components/Evidence/CaptureScreenshotModal';
import {
  RefreshCw,
  PackageCheck,
  Upload,
  Download,
  BarChart3,
  AlertTriangle,
  Plus,
  Camera,
  Calendar,
  Trash2,
  X,
  Loader2
} from 'lucide-react';

const EvidenceHarvesting: React.FC = () => {
  // State
  const [sources, setSources] = useState<EvidenceSource[]>([]);
  const [evidenceItems, setEvidenceItems] = useState<EvidenceItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<EvidenceItem[]>([]);
  const [stats, setStats] = useState<EvidenceStats | null>(null);
  const [filters, setFilters] = useState<Filters>({});
  const [selectedEvidence, setSelectedEvidence] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddSourceModalOpen, setIsAddSourceModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isScreenshotModalOpen, setIsScreenshotModalOpen] = useState(false);
  const [editSource, setEditSource] = useState<EvidenceSource | null>(null);
  const [syncingSourceIds, setSyncingSourceIds] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [generatingPackages, setGeneratingPackages] = useState<Set<string>>(new Set());
  const [generatedPackages, setGeneratedPackages] = useState<Map<string, any>>(new Map());
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    sourceId: number | null;
    sourceName: string;
    isDeleting: boolean;
    error: string | null;
    evidenceCount: number | null;
    showForceOption: boolean;
  }>({ isOpen: false, sourceId: null, sourceName: '', isDeleting: false, error: null, evidenceCount: null, showForceOption: false });

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  // Apply filters
  useEffect(() => {
    applyFilters();
  }, [evidenceItems, filters]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [sourcesData, itemsData, statsData] = await Promise.all([
        evidenceAPI.getEvidenceSources(),
        evidenceAPI.getEvidenceItems(),
        evidenceAPI.getEvidenceStats()
      ]);

      setSources(sourcesData);
      setEvidenceItems(itemsData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load evidence data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...evidenceItems];

    if (filters.status) {
      filtered = filtered.filter(item => item.source?.status === filters.status);
    }

    if (filters.sourceType) {
      filtered = filtered.filter(item => item.source?.source_type === filters.sourceType);
    }

    if (filters.evidenceType) {
      filtered = filtered.filter(item => item.evidence_type === filters.evidenceType);
    }

    if (filters.validationStatus) {
      filtered = filtered.filter(item => item.validation_status === filters.validationStatus);
    }

    if (filters.isStale !== undefined) {
      filtered = filtered.filter(item => item.is_stale === filters.isStale);
    }

    setFilteredItems(filtered);
  };

  const handleSync = async (sourceId: number) => {
    setSyncingSourceIds(prev => new Set(prev).add(sourceId));

    try {
      await evidenceAPI.syncSource(sourceId);

      // Poll for status updates (max 3 minutes)
      const maxPolls = 60; // 60 polls * 3 seconds = 3 minutes
      let pollCount = 0;

      const pollInterval = setInterval(async () => {
        pollCount++;

        try {
          const updatedSource = await evidenceAPI.getEvidenceSourceById(sourceId);

          // Stop polling if status changed from 'syncing' or max time reached
          if (updatedSource.status !== 'syncing' || pollCount >= maxPolls) {
            clearInterval(pollInterval);
            await loadData();
            setSyncingSourceIds(prev => {
              const newSet = new Set(prev);
              newSet.delete(sourceId);
              return newSet;
            });

            if (pollCount >= maxPolls && updatedSource.status === 'syncing') {
              console.warn(`Sync for source ${sourceId} timed out after 3 minutes`);
            }
          }
        } catch (error) {
          console.error('Error polling sync status:', error);
          clearInterval(pollInterval);
          setSyncingSourceIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(sourceId);
            return newSet;
          });
        }
      }, 3000);
    } catch (error) {
      console.error('Sync failed:', error);
      setSyncingSourceIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(sourceId);
        return newSet;
      });
    }
  };

  const handleSyncAll = async () => {
    setIsSyncingAll(true);

    try {
      const activeSources = sources.filter(s => s.status === 'active');
      const sourceIds = activeSources.map(s => s.id);
      setSyncingSourceIds(new Set(sourceIds));

      await evidenceAPI.syncAllSources();

      // Poll for status updates (max 3 minutes)
      const maxPolls = 60;
      let pollCount = 0;

      const pollInterval = setInterval(async () => {
        pollCount++;

        try {
          const updatedSources = await evidenceAPI.getEvidenceSources();
          const stillSyncing = updatedSources.filter(s =>
            sourceIds.includes(s.id) && s.status === 'syncing'
          );

          // Stop polling if all sources done or max time reached
          if (stillSyncing.length === 0 || pollCount >= maxPolls) {
            clearInterval(pollInterval);
            await loadData();
            setSyncingSourceIds(new Set());
            setIsSyncingAll(false);

            if (pollCount >= maxPolls && stillSyncing.length > 0) {
              console.warn(`Sync all timed out after 3 minutes with ${stillSyncing.length} sources still syncing`);
            }
          }
        } catch (error) {
          console.error('Error polling sync all status:', error);
          clearInterval(pollInterval);
          setSyncingSourceIds(new Set());
          setIsSyncingAll(false);
        }
      }, 3000);
    } catch (error) {
      console.error('Sync all failed:', error);
      setSyncingSourceIds(new Set());
      setIsSyncingAll(false);
    }
  };

  const handleEvidenceClick = (item: EvidenceItem) => {
    setSelectedEvidence(item.id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEvidence(null);
  };

  const handleModalUpdate = () => {
    loadData();
  };

  const handleResetFilters = () => {
    setFilters({});
  };

  const handleEdit = (source: EvidenceSource) => {
    setEditSource(source);
    setIsAddSourceModalOpen(true);
  };

  const handleDeleteClick = (sourceId: number) => {
    const source = sources.find(s => s.id === sourceId);
    setDeleteModalState({
      isOpen: true,
      sourceId,
      sourceName: source?.source_name || 'this source',
      isDeleting: false,
      error: null,
      evidenceCount: null,
      showForceOption: false
    });
  };

  const handleDeleteConfirm = async (force: boolean = false) => {
    if (!deleteModalState.sourceId) return;

    setDeleteModalState(prev => ({ ...prev, isDeleting: true, error: null }));

    try {
      await evidenceAPI.deleteEvidenceSource(deleteModalState.sourceId, force);
      toast.success(force ? 'Evidence source and all items deleted successfully' : 'Evidence source deleted successfully');
      setDeleteModalState({ isOpen: false, sourceId: null, sourceName: '', isDeleting: false, error: null, evidenceCount: null, showForceOption: false });
      await loadData();
    } catch (error: any) {
      console.error('Failed to delete source:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete evidence source';
      const evidenceCount = error.response?.data?.evidenceCount || null;

      // If there are evidence items, show force delete option
      if (evidenceCount && evidenceCount > 0) {
        setDeleteModalState(prev => ({
          ...prev,
          isDeleting: false,
          error: errorMessage,
          evidenceCount,
          showForceOption: true
        }));
      } else {
        setDeleteModalState(prev => ({ ...prev, isDeleting: false, error: errorMessage }));
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({ isOpen: false, sourceId: null, sourceName: '', isDeleting: false, error: null, evidenceCount: null, showForceOption: false });
  };

  const handleAddSourceSuccess = async (sourceId?: number, shouldSync?: boolean) => {
    await loadData();
    setIsAddSourceModalOpen(false);
    setEditSource(null);

    // Auto-sync/scan after saving
    if (shouldSync && sourceId) {
      setTimeout(() => {
        handleSync(sourceId);
      }, 500);
    }
  };

  const handleGeneratePackage = async (packageType: string) => {
    // Check if already generating this type
    if (generatingPackages.has(packageType)) {
      alert(`${packageType} package is already being generated. Please wait...`);
      return;
    }

    try {
      // Filter evidence based on package type
      let relevantEvidence = evidenceItems;

      if (packageType !== 'complete') {
        // Filter by compliance tags matching the framework
        relevantEvidence = evidenceItems.filter(item =>
          item.compliance_tags?.some(tag =>
            tag.toUpperCase().includes(packageType.toUpperCase())
          ) || item.validation_status === 'approved'
        );
      }

      if (relevantEvidence.length === 0) {
        alert(`No evidence items found for ${packageType} framework.\n\nTip: Add compliance tags to evidence items or approve some items to include them in packages.`);
        return;
      }

      // Mark as generating
      setGeneratingPackages(prev => new Set(prev).add(packageType));

      const packageData = {
        package_name: `${packageType} Evidence Package - ${new Date().toLocaleDateString()}`,
        package_description: `Automated evidence package for ${packageType} compliance framework`,
        package_type: packageType === 'complete' ? 'complete' as const : packageType.toLowerCase() as any,
        evidence_item_ids: relevantEvidence.map(e => e.id),
        regulatory_frameworks: [packageType === 'complete' ? 'ALL' : packageType],
        compliance_tags: []
      };

      const pkg = await evidenceAPI.createEvidencePackage(packageData);

      // Poll for package generation status
      const pollInterval = setInterval(async () => {
        try {
          const updatedPkg = await evidenceAPI.getEvidencePackageById(pkg.id);

          if (updatedPkg.generation_status === 'completed') {
            clearInterval(pollInterval);
            setGeneratingPackages(prev => {
              const next = new Set(prev);
              next.delete(packageType);
              return next;
            });
            setGeneratedPackages(prev => new Map(prev).set(packageType, updatedPkg));
            alert(`✅ ${packageType} package ready for download!\n\nPackage: ${updatedPkg.package_name}\nSize: ${((updatedPkg.total_package_size_bytes || 0) / 1024 / 1024).toFixed(2)} MB\nEvidence items: ${(updatedPkg.total_evidence_count || 0)}`);
          } else if (updatedPkg.generation_status === 'failed') {
            clearInterval(pollInterval);
            setGeneratingPackages(prev => {
              const next = new Set(prev);
              next.delete(packageType);
              return next;
            });
            alert(`❌ Package generation failed!\n\nPlease try again or contact support if the issue persists.`);
          }
        } catch (pollError) {
          console.error('Error polling package status:', pollError);
        }
      }, 3000); // Poll every 3 seconds

      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (generatingPackages.has(packageType)) {
          setGeneratingPackages(prev => {
            const next = new Set(prev);
            next.delete(packageType);
            return next;
          });
          alert(`⏱️ Package generation is taking longer than expected.\n\nYou can check the status in the Evidence Packages section or try again later.`);
        }
      }, 300000);

    } catch (error: any) {
      console.error('Package generation failed:', error);
      setGeneratingPackages(prev => {
        const next = new Set(prev);
        next.delete(packageType);
        return next;
      });

      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      alert(`❌ Failed to create evidence package\n\nError: ${errorMessage}\n\nPlease check your evidence items and try again.`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Page Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Evidence Harvesting</h1>
          <p className="text-gray-600">
            Automated evidence collection and management for compliance audits
          </p>
        </div>
        <button
          onClick={() => setIsAddSourceModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Source
        </button>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Sources</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total_sources}</p>
                <p className="text-xs text-green-600 mt-1">
                  {stats.active_sources} active
                </p>
              </div>
              <PackageCheck className="h-12 w-12 text-blue-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Evidence</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total_evidence}</p>
                <p className="text-xs text-green-600 mt-1">
                  {stats.validated_evidence} validated
                </p>
              </div>
              <BarChart3 className="h-12 w-12 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Recent Collections</p>
                <p className="text-3xl font-bold text-gray-900">{stats.recent_collections}</p>
                <p className="text-xs text-gray-600 mt-1">Last 30 days</p>
              </div>
              <Download className="h-12 w-12 text-purple-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Stale Evidence</p>
                <p className="text-3xl font-bold text-gray-900">{stats.stale_evidence}</p>
                <p className="text-xs text-orange-600 mt-1">
                  {stats.stale_evidence > 0 ? 'Needs attention' : 'All current'}
                </p>
              </div>
              <AlertTriangle className="h-12 w-12 text-orange-600 opacity-20" />
            </div>
          </div>
        </div>
      )}

      {/* Evidence Sources Section */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Evidence Sources</h2>
            <button
              onClick={handleSyncAll}
              disabled={isSyncingAll || sources.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${isSyncingAll ? 'animate-spin' : ''}`} />
              {isSyncingAll ? 'Syncing All...' : 'Sync All Sources'}
            </button>
          </div>

          <EvidenceSourceGrid
            sources={sources}
            onSync={handleSync}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            syncingSourceIds={syncingSourceIds}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Evidence Items Section */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Collected Evidence</h2>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {filteredItems.length} of {evidenceItems.length} items
              </span>
            </div>
          </div>

          <div className="mb-6">
            <EvidenceFilters
              filters={filters}
              onFilterChange={setFilters}
              onReset={handleResetFilters}
            />
          </div>

          <EvidenceList
            items={filteredItems}
            onItemClick={handleEvidenceClick}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Auditor Packages Section */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Generate Audit Package</h2>
          <p className="text-sm text-gray-600 mb-6">
            Create compliance evidence packages for auditors with automated checklists and metadata.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Complete Package */}
            <div className="relative">
              <button
                onClick={() => generatedPackages.has('complete') ?
                  window.open(`/api/evidence/packages/${generatedPackages.get('complete').id}/download`, '_blank') :
                  handleGeneratePackage('complete')
                }
                disabled={generatingPackages.has('complete')}
                className={`w-full flex flex-col items-center justify-center p-6 border-2 rounded-lg transition-all group ${
                  generatedPackages.has('complete')
                    ? 'border-green-500 bg-green-50'
                    : generatingPackages.has('complete')
                    ? 'border-gray-300 bg-gray-50 cursor-wait'
                    : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                {generatingPackages.has('complete') ? (
                  <RefreshCw className="h-8 w-8 text-blue-600 mb-2 animate-spin" />
                ) : generatedPackages.has('complete') ? (
                  <Download className="h-8 w-8 text-green-600 mb-2" />
                ) : (
                  <PackageCheck className="h-8 w-8 text-gray-400 group-hover:text-blue-600 mb-2" />
                )}
                <span className="text-sm font-medium text-gray-900">
                  {generatedPackages.has('complete') ? 'Download Ready' : 'Complete Package'}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  {generatingPackages.has('complete') ? 'Generating...' : 'All evidence'}
                </span>
              </button>
            </div>

            {/* SOC 2 Package */}
            <div className="relative">
              <button
                onClick={() => generatedPackages.has('SOC2') ?
                  window.open(`/api/evidence/packages/${generatedPackages.get('SOC2').id}/download`, '_blank') :
                  handleGeneratePackage('SOC2')
                }
                disabled={generatingPackages.has('SOC2')}
                className={`w-full flex flex-col items-center justify-center p-6 border-2 rounded-lg transition-all group ${
                  generatedPackages.has('SOC2')
                    ? 'border-green-500 bg-green-50'
                    : generatingPackages.has('SOC2')
                    ? 'border-gray-300 bg-gray-50 cursor-wait'
                    : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                {generatingPackages.has('SOC2') ? (
                  <RefreshCw className="h-8 w-8 text-blue-600 mb-2 animate-spin" />
                ) : generatedPackages.has('SOC2') ? (
                  <Download className="h-8 w-8 text-green-600 mb-2" />
                ) : (
                  <PackageCheck className="h-8 w-8 text-gray-400 group-hover:text-blue-600 mb-2" />
                )}
                <span className="text-sm font-medium text-gray-900">
                  {generatedPackages.has('SOC2') ? 'Download Ready' : 'SOC 2 Package'}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  {generatingPackages.has('SOC2') ? 'Generating...' : 'Trust criteria'}
                </span>
              </button>
            </div>

            {/* ISO 27001 Package */}
            <div className="relative">
              <button
                onClick={() => generatedPackages.has('ISO27001') ?
                  window.open(`/api/evidence/packages/${generatedPackages.get('ISO27001').id}/download`, '_blank') :
                  handleGeneratePackage('ISO27001')
                }
                disabled={generatingPackages.has('ISO27001')}
                className={`w-full flex flex-col items-center justify-center p-6 border-2 rounded-lg transition-all group ${
                  generatedPackages.has('ISO27001')
                    ? 'border-green-500 bg-green-50'
                    : generatingPackages.has('ISO27001')
                    ? 'border-gray-300 bg-gray-50 cursor-wait'
                    : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                {generatingPackages.has('ISO27001') ? (
                  <RefreshCw className="h-8 w-8 text-blue-600 mb-2 animate-spin" />
                ) : generatedPackages.has('ISO27001') ? (
                  <Download className="h-8 w-8 text-green-600 mb-2" />
                ) : (
                  <PackageCheck className="h-8 w-8 text-gray-400 group-hover:text-blue-600 mb-2" />
                )}
                <span className="text-sm font-medium text-gray-900">
                  {generatedPackages.has('ISO27001') ? 'Download Ready' : 'ISO 27001'}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  {generatingPackages.has('ISO27001') ? 'Generating...' : 'Information security'}
                </span>
              </button>
            </div>

            {/* PCI DSS Package */}
            <div className="relative">
              <button
                onClick={() => generatedPackages.has('PCI_DSS') ?
                  window.open(`/api/evidence/packages/${generatedPackages.get('PCI_DSS').id}/download`, '_blank') :
                  handleGeneratePackage('PCI_DSS')
                }
                disabled={generatingPackages.has('PCI_DSS')}
                className={`w-full flex flex-col items-center justify-center p-6 border-2 rounded-lg transition-all group ${
                  generatedPackages.has('PCI_DSS')
                    ? 'border-green-500 bg-green-50'
                    : generatingPackages.has('PCI_DSS')
                    ? 'border-gray-300 bg-gray-50 cursor-wait'
                    : 'border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                {generatingPackages.has('PCI_DSS') ? (
                  <RefreshCw className="h-8 w-8 text-blue-600 mb-2 animate-spin" />
                ) : generatedPackages.has('PCI_DSS') ? (
                  <Download className="h-8 w-8 text-green-600 mb-2" />
                ) : (
                  <PackageCheck className="h-8 w-8 text-gray-400 group-hover:text-blue-600 mb-2" />
                )}
                <span className="text-sm font-medium text-gray-900">
                  {generatedPackages.has('PCI_DSS') ? 'Download Ready' : 'PCI DSS'}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  {generatingPackages.has('PCI_DSS') ? 'Generating...' : 'Payment security'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Evidence Actions Section */}
      <div className="mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Evidence Actions</h2>
          <p className="text-sm text-gray-600 mb-6">
            Quick actions for manual evidence collection and management.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => setIsUploadModalOpen(true)}
              className="flex flex-col items-center justify-center p-6 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
            >
              <Upload className="h-8 w-8 text-gray-400 group-hover:text-blue-600 mb-2" />
              <span className="text-sm font-medium text-gray-900">Upload Manual Evidence</span>
              <span className="text-xs text-gray-500 mt-1">Audit reports, certifications</span>
            </button>

            <button
              onClick={() => setIsScreenshotModalOpen(true)}
              className="flex flex-col items-center justify-center p-6 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all group"
            >
              <Camera className="h-8 w-8 text-gray-400 group-hover:text-green-600 mb-2" />
              <span className="text-sm font-medium text-gray-900">Capture Screenshot</span>
              <span className="text-xs text-gray-500 mt-1">Timestamped control evidence</span>
            </button>

            <button
              onClick={() => alert('Auto-capture scheduling coming soon!\n\nThis will allow you to:\n• Schedule daily/weekly/monthly captures\n• Set up auto-sync reminders\n• Track evidence expiration dates')}
              className="flex flex-col items-center justify-center p-6 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all group"
            >
              <Calendar className="h-8 w-8 text-gray-400 group-hover:text-purple-600 mb-2" />
              <span className="text-sm font-medium text-gray-900">Schedule Auto-Capture</span>
              <span className="text-xs text-gray-500 mt-1">Automated evidence collection</span>
            </button>
          </div>
        </div>
      </div>

      {/* Evidence Detail Modal */}
      <EvidenceDetailModal
        evidenceId={selectedEvidence}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onUpdate={handleModalUpdate}
      />

      {/* Add/Edit Source Modal */}
      <AddSourceModal
        isOpen={isAddSourceModalOpen}
        onClose={() => {
          setIsAddSourceModalOpen(false);
          setEditSource(null);
        }}
        onSuccess={handleAddSourceSuccess}
        editSource={editSource || undefined}
      />

      {/* Upload Evidence Modal */}
      <UploadEvidenceModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onSuccess={() => {
          loadData();
          setIsUploadModalOpen(false);
        }}
      />

      {/* Capture Screenshot Modal */}
      <CaptureScreenshotModal
        isOpen={isScreenshotModalOpen}
        onClose={() => setIsScreenshotModalOpen(false)}
        onSuccess={() => {
          loadData();
          setIsScreenshotModalOpen(false);
        }}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalState.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={handleDeleteCancel} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6 transform transition-all">
              <button
                onClick={handleDeleteCancel}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              <div className="flex items-center gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Evidence Source</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>

              <p className="text-gray-600 mb-4">
                Are you sure you want to delete <span className="font-semibold">"{deleteModalState.sourceName}"</span>?
              </p>

              {deleteModalState.error && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Warning</p>
                      <p className="text-sm text-amber-700 mt-1">{deleteModalState.error}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3 mt-6">
                {deleteModalState.showForceOption ? (
                  <>
                    <button
                      onClick={() => handleDeleteConfirm(true)}
                      disabled={deleteModalState.isDeleting}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {deleteModalState.isDeleting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Delete Source & {deleteModalState.evidenceCount} Evidence Items
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleDeleteCancel}
                      disabled={deleteModalState.isDeleting}
                      className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <div className="flex gap-3">
                    <button
                      onClick={handleDeleteCancel}
                      disabled={deleteModalState.isDeleting}
                      className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDeleteConfirm(false)}
                      disabled={deleteModalState.isDeleting}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {deleteModalState.isDeleting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Delete Source
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvidenceHarvesting;
